// The D-10 display conventions for the commercial side (BRK-S305): dates as
// m/d/yyyy and money with a thousands separator, for display only. The wire
// and storage stay an ISO date and a plain number. The bond pages keep their
// own (src/lib/bondValueDisplay.ts), which submission code may not import
// (test/lib/submissions-boundary.test.ts).

import { formatMoneyInput } from "@/lib/currency";

/**
 * A date as m/d/yyyy, without leading zeros. Read from the value's own text,
 * not through `Date`, so a date-only value can't shift a day in the viewer's
 * timezone. Anything that isn't an ISO date is shown as stored.
 */
export function formatIsoDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${Number(month)}/${Number(day)}/${year}`;
}

/** US dollars with a thousands separator and cents. PMS sends money as a
 *  decimal string or a number; a value that isn't a number is shown as sent. */
export function formatUsd(value: number | string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** A limit in whole dollars ("$1,000,000"), keeping any cents it has: a
 *  coverage limit reads cleaner without ".00" (BRK-S347). */
export function formatLimit(value: number | string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });
}

/** A value as a read-only field's box shows it (BRK-S349): its text, and the
 *  unit its editable field draws inside the box. No value reads as missing. */
export interface BoxValue {
  value: string | undefined;
  /** Before the value, as `$` sits in a currency field's box. */
  start?: string;
  /** After it, as `%` sits in a percent field's. */
  end?: string;
}

/**
 * A number as `NumericField` shows it, kind for kind: money grouped with `$`
 * before it and cents only when it has them (`formatMoneyInput`), a percent
 * with `%` after it, a count as it is. 0 is a value; a number nobody gave has
 * none, and no unit.
 */
export function numberBox(
  kind: "currency" | "percent" | "number",
  value: number | undefined,
): BoxValue {
  if (value === undefined || !Number.isFinite(value)) {
    return { value: undefined };
  }
  if (kind === "currency")
    return { value: formatMoneyInput(value), start: "$" };
  if (kind === "percent") return { value: String(value), end: "%" };
  return { value: String(value) };
}
